import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    ArrowLeft, KeyRound, MapPin, AlertCircle, CheckCircle2, PlayCircle, PauseCircle, Camera, Video,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import MediaUpload from "@/components/MediaUpload";
import { TASK_STATE_LABELS, TASK_TYPE_LABELS } from "@/lib/dictionaries";

const DEFAULT_POINTS = [
    { id: "keys_in", label: "Dónde están las llaves" },
    { id: "kitchen", label: "Cocina" },
    { id: "main_bath", label: "Baño principal" },
    { id: "living", label: "Salón" },
    { id: "bedroom", label: "Dormitorios" },
    { id: "trash", label: "Cubo de basura vaciado" },
    { id: "keys_out", label: "Dónde dejas las llaves" },
];

export default function OperatorTaskDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [task, setTask] = useState(null);
    const [points, setPoints] = useState(DEFAULT_POINTS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actualMin, setActualMin] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get(`/operator/tasks/${id}`)
            .then((r) => {
                setTask(r.data);
                setActualMin(r.data.actual_minutes || "");
                setNotes(r.data.notes || "");
                // Photo points come from property
                setPoints(r.data.property?.photo_points || DEFAULT_POINTS);
            })
            .catch((e) => setError(formatApiError(e)))
            .finally(() => setLoading(false));
    };
    useEffect(load, [id]); // eslint-disable-line

    if (loading || !task) return <div className="zentra-skeleton h-96" />;
    const media = task.media || [];
    const findMedia = (type, point_id) => media.find((m) => m.type === type && (point_id ? m.point_id === point_id : true));
    const videoBefore = findMedia("video_before");
    const videoAfter = findMedia("video_after");
    const freePhotos = media.filter((m) => m.type === "photo" && !m.point_id);

    const setTaskState = async (newState) => {
        try {
            await api.patch(`/operator/tasks/${task.id}`, { state: newState });
            load();
        } catch (e) {
            alert(formatApiError(e));
        }
    };

    const saveNotes = async () => {
        setSaving(true);
        try {
            const payload = { notes };
            if (actualMin !== "" && actualMin !== null) payload.actual_minutes = Number(actualMin);
            await api.patch(`/operator/tasks/${task.id}`, payload);
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const onMediaUploaded = () => load();

    // Folder structure in Cloudinary: zentra/cleanings/{property_code}/{YYYY-MM-DD}/
    // This way each cleaning has its own browsable folder for admin + client portal.
    const folder = task
        ? `zentra/cleanings/${(task.property?.code || "misc").replace(/[^a-zA-Z0-9_-]/g, "_")}/${task.scheduled_date || "undated"}`
        : "zentra/misc";

    // Block completion if missing required media
    const missingRequired = [];
    if (!videoBefore) missingRequired.push("Vídeo inicial");
    points.forEach((pt) => {
        if (!findMedia("photo", pt.id)) missingRequired.push(`Foto: ${pt.label}`);
    });
    if (!videoAfter) missingRequired.push("Vídeo final");
    const canComplete = missingRequired.length === 0;

    return (
        <div className="space-y-5" data-testid="op-task-detail">
            <Link to="/operario/tareas" className="text-xs text-zentra-muted hover:text-zentra-ink inline-flex items-center gap-1.5">
                <ArrowLeft size={12} /> Volver al listado
            </Link>

            {/* Header */}
            <div className="zentra-card-static p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <p className="text-[11px] font-mono-zentra tracking-wider text-zentra-muted uppercase">
                            {task.property?.code} · {task.scheduled_date}
                            {task.start_time && ` · ${task.start_time}${task.end_time ? `→${task.end_time}` : ""}`}
                        </p>
                        <h2 className="font-display text-2xl font-bold text-zentra-ink tracking-tight mt-1">{task.property?.name}</h2>
                        {task.property?.address && (
                            <p className="text-sm text-zentra-muted mt-1 inline-flex items-center gap-1.5">
                                <MapPin size={13} /> {task.property.address}
                            </p>
                        )}
                        {task.key_access && (
                            <p className="text-xs text-zentra-muted mt-1 inline-flex items-center gap-1.5">
                                <KeyRound size={11} /> {task.key_access}
                            </p>
                        )}
                    </div>
                    <StateBadge value={task.state} dict={TASK_STATE_LABELS} />
                </div>
                <div className="flex items-center gap-2 flex-wrap pt-4 mt-4 border-t border-zentra-border">
                    {task.state === "pending" && (
                        <button onClick={() => setTaskState("in_progress")} className="zentra-btn-primary" data-testid="op-detail-start">
                            <PlayCircle size={16} /> Empezar limpieza
                        </button>
                    )}
                    {task.state === "in_progress" && (
                        <>
                            <button onClick={() => setTaskState("completed")} disabled={!canComplete} className="zentra-btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="op-detail-complete">
                                <CheckCircle2 size={16} /> Marcar completada
                            </button>
                            <button onClick={() => setTaskState("pending")} className="zentra-btn-ghost">
                                <PauseCircle size={14} /> Pausar
                            </button>
                        </>
                    )}
                    {task.state === "completed" && (
                        <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm">
                            <CheckCircle2 size={16} /> Tarea completada
                        </span>
                    )}
                </div>
                {!canComplete && task.state === "in_progress" && (
                    <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-900">
                        <p className="font-semibold inline-flex items-center gap-1"><AlertCircle size={12} /> Falta antes de completar:</p>
                        <ul className="list-disc list-inside mt-1">
                            {missingRequired.map((m) => <li key={m}>{m}</li>)}
                        </ul>
                    </div>
                )}
            </div>

            {/* Service info */}
            {task.service_type_text && (
                <div className="zentra-card-static p-4">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted">Tipo de servicio</p>
                    <p className="font-display text-lg font-bold text-zentra-primary mt-1">{task.service_type_text}</p>
                </div>
            )}

            {/* Fixed instructions */}
            {task.property?.fixed_instructions && (
                <div className="zentra-card-static p-4">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-accent-strong mb-1.5">Instrucciones fijas de la propiedad</p>
                    <p className="text-sm text-zentra-ink whitespace-pre-wrap font-mono-zentra leading-relaxed">{task.property.fixed_instructions}</p>
                </div>
            )}

            {/* Day instructions */}
            {task.day_instructions && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-3" data-testid="op-day-instructions">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-amber-700 mb-1 inline-flex items-center gap-1">
                        <AlertCircle size={11} /> Instrucciones del día
                    </p>
                    <p className="text-xs text-amber-900 whitespace-pre-wrap">{task.day_instructions}</p>
                </div>
            )}

            {/* Vídeo inicial */}
            <Section title="1 · Vídeo inicial · cómo está al llegar" icon={Video}>
                <div className="max-w-md">
                    <MediaUpload
                        type="video_before"
                        label="Grabar vídeo inicial"
                        taskId={task.id}
                        folder={folder}
                        existing={videoBefore}
                        onUploaded={onMediaUploaded}
                        onDelete={onMediaUploaded}
                    />
                </div>
            </Section>

            {/* Fotos obligatorias */}
            <Section title="2 · Fotos obligatorias" icon={Camera}>
                <p className="text-xs text-zentra-muted mb-3">Saca una foto de cada punto. Estos puntos los define el administrador en la propiedad.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {points.map((pt) => (
                        <div key={pt.id}>
                            <MediaUpload
                                type="photo"
                                pointId={pt.id}
                                label={pt.label}
                                taskId={task.id}
                                folder={folder}
                                existing={findMedia("photo", pt.id)}
                                onUploaded={onMediaUploaded}
                                onDelete={onMediaUploaded}
                            />
                            <p className="text-[11px] text-zentra-ink text-center mt-1.5 font-medium">{pt.label}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* Fotos libres */}
            <Section title="3 · Fotos extra (opcional)" icon={Camera}>
                <p className="text-xs text-zentra-muted mb-3">Detalles, incidencias o cualquier cosa que quieras documentar.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {freePhotos.map((m) => (
                        <MediaUpload key={m.id} type="photo" taskId={task.id} folder={folder} existing={m} onDelete={onMediaUploaded} onUploaded={onMediaUploaded} />
                    ))}
                    <MediaUpload type="photo" taskId={task.id} folder={folder} label="Añadir foto extra" onUploaded={onMediaUploaded} />
                </div>
            </Section>

            {/* Vídeo final */}
            <Section title="4 · Vídeo final · cómo queda al terminar" icon={Video}>
                <div className="max-w-md">
                    <MediaUpload
                        type="video_after"
                        label="Grabar vídeo final"
                        taskId={task.id}
                        folder={folder}
                        existing={videoAfter}
                        onUploaded={onMediaUploaded}
                        onDelete={onMediaUploaded}
                    />
                </div>
            </Section>

            {/* Notes & time */}
            <div className="zentra-card-static p-5">
                <h3 className="font-display font-semibold text-zentra-ink mb-3">Notas y tiempo real</h3>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className="zentra-label">Minutos reales empleados</label>
                        <input type="number" min="0" className="zentra-input" value={actualMin} onChange={(e) => setActualMin(e.target.value)} placeholder={String(task.estimated_minutes || 60)} data-testid="op-detail-actual-min" />
                    </div>
                    <div className="md:col-span-1">
                        <label className="zentra-label">Notas</label>
                        <input className="zentra-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Algo a destacar…" data-testid="op-detail-notes" />
                    </div>
                </div>
                <button onClick={saveNotes} className="zentra-btn-ghost mt-3" disabled={saving} data-testid="op-detail-save-notes">
                    {saving ? "Guardando…" : "Guardar"}
                </button>
            </div>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <div className="zentra-card-static p-5">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-zentra-border">
                <Icon size={16} className="text-zentra-primary" />
                <h3 className="font-display font-semibold text-zentra-ink">{title}</h3>
            </div>
            {children}
        </div>
    );
}
